/** 申請・タスクに対する操作(承認・却下・完了)。一覧と明細で同じものを使う。 */
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useState } from "react";

import type { Sla, WfRequest, WfTask } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText } from "./hooks";
import { useEquipment } from "./loanHooks";
import { useInvalidateWf, useRequest, useWfPerms } from "./wfHooks";

/** SLA の印。超過しているものだけ赤く出す(上限は設定 sla)。 */
export function SlaTag({ sla }: { sla: Sla | undefined }) {
  if (!sla) return null;
  const hours = `${sla.elapsed_hours}h / ${sla.limit_hours}h`;
  if (sla.over && !sla.done)
    return (
      <Tooltip title={`上限 ${sla.limit_hours} 時間を超えている(${hours})`}>
        <Tag color="red">SLA超過</Tag>
      </Tooltip>
    );
  if (sla.done) return <Tag color="green">{hours}</Tag>;
  return <Tag>{hours}</Tag>;
}

/** 自動実行失敗の印(server: app/hr/constants.py の AUTO_FAILED_PREFIX と同じ文字列)。 */
const AUTO_FAILED = "[自動実行失敗]";
/** idp のコネクタが閉じたタスクの completed_by は `connector:<キー>`。 */
const BY_CONNECTOR = "connector:";

/**
 * 自動(コネクタ)のタスクの進み具合。
 *
 * 起票しただけ = コネクタ待ち / idp が閉じた = 自動で完了 /
 * 失敗した = 未完了のまま理由が result に入る(人が手で完了にできる)。
 */
export function AutoStateTag({ task }: { task: WfTask }) {
  if (task.mode !== "auto") return null;
  if (task.status !== "done" && task.result.startsWith(AUTO_FAILED))
    return (
      <Tooltip title={task.result}>
        <Tag color="red">自動実行失敗</Tag>
      </Tooltip>
    );
  if (task.status === "done" && task.completed_by.startsWith(BY_CONNECTOR))
    return (
      <Tooltip title={task.result || task.completed_by}>
        <Tag color="green">自動で完了</Tag>
      </Tooltip>
    );
  if (task.status !== "done")
    return (
      <Tooltip title="idp が定期的にイベントを拾って実行する(手で完了にしてもよい)">
        <Tag color="blue">コネクタ待ち</Tag>
      </Tooltip>
    );
  return null;
}

/**
 * 失敗した自動(コネクタ)タスクの再実行。承認者 / 管理者だけ。
 *
 * 押すと `task.requested` を積み直すだけで、実行は idp のジョブ(60 秒ごと)。実行の
 * 依頼がまだ残っているときはサーバーが 409 で断る(二重実行を防ぐ)。失敗の理由は
 * `result` に残っているので、押す前に読めるよう Popconfirm に出す。
 */
export function RerunTask({ task }: { task: WfTask }) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canApprove } = useWfPerms();

  const rerun = useMutation({
    mutationFn: () => api.rerunTask(task.id),
    onSuccess: () => {
      void message.success(`タスク #${task.id} の再実行を依頼した(実行は最大 1 分後)`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const failed = task.mode === "auto" && task.status !== "done" && task.result.startsWith(AUTO_FAILED);
  if (!canApprove || !failed) return null;
  return (
    <Popconfirm
      title="この自動タスクをもう一度実行する"
      description={
        <div style={{ maxWidth: 360, whiteSpace: "pre-wrap" }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            前回の理由: {task.result}
          </Typography.Text>
        </div>
      }
      okText="再実行する"
      cancelText="やめる"
      okButtonProps={{ loading: rerun.isPending }}
      onConfirm={() => rerun.mutate()}
    >
      <Button size="small" loading={rerun.isPending}>
        再実行
      </Button>
    </Popconfirm>
  );
}

/** 却下・取り下げた申請を消す(hr:admin)。月次の棚卸しに残さないため。承認済みは消せない(履歴)。 */
export function DeleteClosed({
  req,
  size = "small",
  onDone,
}: {
  req: WfRequest;
  size?: "small" | "middle";
  onDone?: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canAdmin } = useWfPerms();
  const del = useMutation({
    mutationFn: () => api.deleteRequest(req.id),
    onSuccess: () => {
      void message.success(`${closedLabel(req.status)}申請 #${req.id} を消した`);
      invalidate();
      onDone?.();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  if (!canAdmin || (req.status !== "rejected" && req.status !== "withdrawn")) return null;
  return (
    <Popconfirm
      title={`${closedLabel(req.status)}申請 #${req.id} を消す`}
      description={
        <div style={{ maxWidth: 360 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            承認票ごと消え、戻せない(消した記録は監査に残る)。
          </Typography.Text>
        </div>
      }
      okText="消す"
      cancelText="やめる"
      okButtonProps={{ danger: true, loading: del.isPending }}
      onConfirm={() => del.mutate()}
    >
      <Button size={size} danger loading={del.isPending}>
        削除
      </Button>
    </Popconfirm>
  );
}

function closedLabel(status: string): string {
  return status === "withdrawn" ? "取り下げた" : "却下した";
}

/**
 * 承認待ちの申請の取り下げ(申請者本人か hr:admin)。理由は任意。多段承認の途中でもよい。
 *
 * 入社申請で、対象者が入社前・この申請以外に何も無い(サーバーの `employee_deletable`)ときだけ
 * 「従業員の登録も消す」を出す。一覧の行には権限情報が無いので、詳細を引いてボタンを出し分ける。
 */
export function Withdraw({
  req,
  size = "small",
  onDone,
}: {
  req: WfRequest;
  size?: "small" | "middle";
  onDone?: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canOperate } = useWfPerms();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [deleteEmployee, setDeleteEmployee] = useState(false);
  const detail = useRequest(canOperate && req.status === "pending" ? req.id : undefined);
  const deletable = open && !!detail.data?.employee_deletable;

  const withdraw = useMutation({
    mutationFn: () => api.withdrawRequest(req.id, reason, deletable && deleteEmployee),
    onSuccess: (res) => {
      void message.success(
        res.employee_deleted
          ? `申請 #${req.id} を取り下げ、${req.employee_name} の登録も消した`
          : `申請 #${req.id} を取り下げた`,
      );
      setOpen(false);
      setReason("");
      setDeleteEmployee(false);
      invalidate();
      onDone?.();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (!canOperate || req.status !== "pending" || !detail.data?.withdrawable) return null;
  return (
    <>
      <Button size={size} onClick={() => {
        setReason("");
        setDeleteEmployee(false);
        setOpen(true);
      }}>
        取り下げ
      </Button>
      <Modal
        open={open}
        title={`申請 #${req.id} を取り下げる`}
        okText="取り下げる"
        cancelText="やめる"
        okButtonProps={{ danger: true }}
        confirmLoading={withdraw.isPending}
        onCancel={() => setOpen(false)}
        onOk={() => withdraw.mutate()}
      >
        <p style={{ marginTop: 0 }}>
          {req.kind === "onboard"
            ? "承認待ちのまま止め、タスクは起票しない。取り下げた申請は承認できない。従業員の登録を残すと、この人に入社申請を出し直すことはできない。出し直す場合は、従業員の登録も消して新しく入力する(残した人は、他に履歴が無ければ hr:admin が取り下げた申請を消したあと、従業員詳細の「削除」で消せる)。"
            : "承認待ちのまま止め、タスクは起票しない。取り下げた申請は承認できない(出し直しはできる)。"}
        </p>
        <Input.TextArea
          rows={3}
          placeholder="取り下げの理由(任意。入社辞退・誤登録など)"
          value={reason}
          maxLength={1000}
          onChange={(e) => setReason(e.target.value)}
        />
        {deletable && (
          <div style={{ marginTop: 12 }} data-withdraw-delete-employee>
            <Checkbox checked={deleteEmployee} onChange={(e) => setDeleteEmployee(e.target.checked)}>
              従業員の登録も消す(入社前で、この申請以外に何も無いとき)
            </Checkbox>
            {deleteEmployee && (
              <Typography.Text type="danger" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                {req.employee_name} の登録と、この申請が消える。戻せない(監査には残る)。
              </Typography.Text>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

/** 承認・却下。承認は hr:approver、却下は理由が必須。 */
export function ApproveReject({ req, size = "small" }: { req: WfRequest; size?: "small" | "middle" }) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canApprove } = useWfPerms();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const approve = useMutation({
    mutationFn: () => api.approveRequest(req.id, reason),
    onSuccess: (res) => {
      void message.success(
        res.status === "approved" ? `申請 #${req.id} が承認された(${res.approvals})` : `承認した(${res.approvals})`,
      );
      setReason("");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const reject = useMutation({
    mutationFn: () => api.rejectRequest(req.id, reason),
    onSuccess: () => {
      void message.success(`申請 #${req.id} を却下した`);
      setRejecting(false);
      setReason("");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (!canApprove || req.status !== "pending") return null;
  return (
    <>
      <Space>
        <Button
          size={size}
          type="primary"
          loading={approve.isPending}
          onClick={() => approve.mutate()}
        >
          承認
        </Button>
        <Button size={size} danger onClick={() => setRejecting(true)}>
          却下
        </Button>
      </Space>
      <Modal
        open={rejecting}
        title={`申請 #${req.id} を却下する`}
        okText="却下する"
        cancelText="やめる"
        okButtonProps={{ danger: true }}
        confirmLoading={reject.isPending}
        onCancel={() => setRejecting(false)}
        onOk={() => {
          if (!reason.trim()) {
            void message.error("却下の理由は必須");
            return;
          }
          reject.mutate();
        }}
      >
        <Input.TextArea
          rows={3}
          placeholder="却下の理由(申請者に残る)"
          value={reason}
          maxLength={1000}
          onChange={(e) => setReason(e.target.value)}
        />
      </Modal>
    </>
  );
}

/** タスクを完了にする。自分が承認した申請のタスクはサーバー側で 403(職務分掌)。
 *
 * 貸与品の手配タスク(loan かつ provision)では、実際に渡した個体を選べる。
 * 個体の在庫は区分 = 工程名で絞る(台帳の品目と備品の区分は同じ値)。
 */
export function CompleteTask({ task }: { task: WfTask }) {
  const api = useApi();
  const invalidate = useInvalidateWf();
  const { canOperate } = useWfPerms();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState("");
  const [equipmentId, setEquipmentId] = useState<number | undefined>();
  const picksUnit = task.loan && task.action === "provision";
  const stock = useEquipment({
    category: task.name,
    status: "available",
    limit: 200,
    enabled: open && picksUnit,
  });

  const done = useMutation({
    mutationFn: () => api.completeTask(task.id, result, picksUnit ? equipmentId : undefined),
    onSuccess: () => {
      void message.success(`タスク #${task.id} を完了にした`);
      setOpen(false);
      setResult("");
      setEquipmentId(undefined);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (!canOperate || task.status === "done") return null;
  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>
        完了にする
      </Button>
      <Modal
        open={open}
        title={`${task.section ? `${task.section}/` : ""}${task.name} を完了にする`}
        okText="完了にする"
        cancelText="やめる"
        confirmLoading={done.isPending}
        onCancel={() => setOpen(false)}
        onOk={() => done.mutate()}
      >
        <p style={{ marginTop: 0 }}>{task.detail}</p>
        {picksUnit && (
          <div style={{ marginBottom: 12 }} data-equipment-picker={task.name}>
            <Typography.Text strong>渡した個体(備品マスタ)</Typography.Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="個体を選ばない(品目だけ台帳に載せる)"
              value={equipmentId}
              loading={stock.isFetching}
              onChange={(v: number | undefined) => setEquipmentId(v)}
              options={(stock.data?.items ?? []).map((e) => ({
                value: e.id,
                label: `${e.asset_no}${e.name ? ` ${e.name}` : ""}`,
              }))}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              区分「{task.name}」の在庫だけ出る。選ぶとその個体が貸与中になる
            </Typography.Text>
          </div>
        )}
        <Input.TextArea
          rows={3}
          placeholder="対応の記録(発行したアカウント名・管理番号など)"
          value={result}
          maxLength={1000}
          onChange={(e) => setResult(e.target.value)}
        />
      </Modal>
    </>
  );
}
